import { z } from 'zod';

import { idSchema, slugSchema } from './common.js';
import { uploadSchema } from './upload.js';

/**
 * Fluxo editorial do artigo:
 *   rascunho → em_revisao → aprovado → publicado
 *   (+ arquivado, terminal a partir de qualquer estado)
 *
 * Cada transição é registrada em article_review_log com autor, timestamp
 * e comentário (obrigatório quando o revisor devolve, opcional quando aprova).
 *
 * Nota Fase 3.6a: o workflow multi-stage só será aplicado a partir do Bloco
 * 3.6b. Por enquanto a API permite que publisher+ alterne livremente entre
 * `rascunho` e `publicado` via PATCH; demais estados ficam disponíveis no
 * enum mas sem políticas atreladas.
 */
export const articleStatusSchema = z.enum([
  'rascunho',
  'em_revisao',
  'aprovado',
  'publicado',
  'arquivado',
]);
export type ArticleStatus = z.infer<typeof articleStatusSchema>;

export const ARTICLE_STATUS_LABELS: Record<ArticleStatus, string> = {
  rascunho: 'Rascunho',
  em_revisao: 'Em revisão',
  aprovado: 'Aprovado',
  publicado: 'Publicado',
  arquivado: 'Arquivado',
};

/**
 * Transições válidas. Tudo que não está aqui é rejeitado pelo backend
 * a partir da Fase 3.6b (validação plena do workflow).
 */
export const VALID_TRANSITIONS: Record<ArticleStatus, ArticleStatus[]> = {
  rascunho: ['em_revisao', 'publicado', 'arquivado'],
  em_revisao: ['rascunho', 'aprovado', 'arquivado'],
  aprovado: ['publicado', 'em_revisao', 'arquivado'],
  publicado: ['rascunho', 'arquivado'],
  arquivado: [],
};

export function canTransition(from: ArticleStatus, to: ArticleStatus): boolean {
  return VALID_TRANSITIONS[from].includes(to);
}

/**
 * Categorias editoriais fixas (aprovadas em Publicacoes_IDPE_Copy).
 * Manter sincronizado com seed do banco.
 */
export const articleCategorySchema = z.enum([
  'marcos-regulatorios',
  'analises-setoriais',
  'cases-destrinchados',
  'metodologia',
  'noticias-do-instituto',
  'imprensa',
]);
export type ArticleCategory = z.infer<typeof articleCategorySchema>;

export const ARTICLE_CATEGORY_LABELS: Record<ArticleCategory, string> = {
  'marcos-regulatorios': 'Marcos Regulatórios',
  'analises-setoriais': 'Análises Setoriais',
  'cases-destrinchados': 'Cases Destrinchados',
  metodologia: 'Metodologia',
  'noticias-do-instituto': 'Notícias do Instituto',
  imprensa: 'Imprensa',
};

/**
 * Resposta full do artigo. `capa` e `ogImage` são uploads populados pelo
 * backend quando existem (evita N+1 no client).
 */
export const articleSchema = z.object({
  id: idSchema,
  slug: slugSchema,
  titulo: z.string().min(1).max(220),
  subtitulo: z.string().max(300).nullable(),
  categoriaSlug: articleCategorySchema,
  capaId: idSchema.nullable(),
  capa: uploadSchema.nullable(),
  resumo: z.string().min(1).max(500),
  corpoHtml: z.string(),
  tempoLeituraMin: z.number().int().min(1).max(180),
  status: articleStatusSchema,
  destaque: z.boolean(),
  tags: z.array(z.string().max(40)),
  seoTitle: z.string().max(70).nullable(),
  seoDesc: z.string().max(200).nullable(),
  ogImageId: idSchema.nullable(),
  ogImage: uploadSchema.nullable(),
  agendadoPara: z.string().datetime({ offset: true }).nullable(),
  publicadoEm: z.string().datetime({ offset: true }).nullable(),
  criadoEm: z.string().datetime({ offset: true }),
  atualizadoEm: z.string().datetime({ offset: true }),
});
export type Article = z.infer<typeof articleSchema>;

/**
 * Payload de criação. `status` default `rascunho`, `tempoLeituraMin` é
 * calculado pelo servidor se omitido.
 */
export const createArticleInputSchema = z.object({
  slug: slugSchema,
  titulo: z.string().min(1).max(220),
  subtitulo: z.string().max(300).optional().or(z.literal('')),
  categoriaSlug: articleCategorySchema,
  capaId: idSchema.nullable().optional(),
  resumo: z.string().min(1).max(500),
  corpoHtml: z.string().min(1),
  tempoLeituraMin: z.number().int().min(1).max(180).optional(),
  status: articleStatusSchema.optional(),
  destaque: z.boolean().optional(),
  tags: z.array(z.string().max(40)).max(20).optional(),
  seoTitle: z.string().max(70).optional().or(z.literal('')),
  seoDesc: z.string().max(200).optional().or(z.literal('')),
  ogImageId: idSchema.nullable().optional(),
  agendadoPara: z.string().datetime({ offset: true }).nullable().optional(),
});
export type CreateArticleInput = z.infer<typeof createArticleInputSchema>;

export const updateArticleInputSchema = createArticleInputSchema.partial().omit({ slug: true });
export type UpdateArticleInput = z.infer<typeof updateArticleInputSchema>;

export const articleTransitionInputSchema = z.object({
  para: articleStatusSchema,
  comentario: z.string().max(2000).optional(),
});
export type ArticleTransitionInput = z.infer<typeof articleTransitionInputSchema>;

/**
 * Query da listagem de artigos. Suporta busca textual (título+resumo),
 * filtros (categoria, status, destaque, tag), paginação, sort.
 */
export const listArticlesQuerySchema = z.object({
  q: z.string().max(120).optional(),
  categoria: articleCategorySchema.optional(),
  status: articleStatusSchema.optional(),
  destaque: z.preprocess(
    (v) => (v === 'true' ? true : v === 'false' ? false : undefined),
    z.boolean().optional(),
  ),
  tag: z.string().max(40).optional(),
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(100).default(20),
  sort: z.string().max(40).optional(),
});
export type ListArticlesQuery = z.infer<typeof listArticlesQuerySchema>;

/**
 * Estima tempo de leitura a partir de HTML. Heurística: 200 palavras/min
 * (padrão editorial brasileiro), mínimo 1 minuto.
 *
 * Strip de tags via regex simples — suficiente para HTML do TipTap (sem
 * scripts ou inline styles bizarros).
 */
export function estimateReadingMinutes(html: string): number {
  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&[a-z]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return 1;
  const words = text.split(' ').filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}
